import Phaser from 'phaser';

export class SoundManager {
  private sceneRef: Phaser.Scene;
  private ctx: AudioContext | null = null;
  private isUnlocked: boolean = false;
  private music: Phaser.Sound.BaseSound | undefined;
  private musicKey: string = 'music-level1';
  private muted: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.sceneRef = scene;
    try {
      this.muted = localStorage.getItem('gz-muted') === '1';
    } catch { /* no storage */ }
    // Global unlock: ANY interaction on the page (any scene, even the menu)
    // starts the music immediately — browsers block audio before that.
    // The handler stays alive: every interaction retries until music is playing
    // (covers interactions that happened before assets finished loading).
    if (typeof window !== 'undefined') {
      const handler = () => this.unlock();
      window.addEventListener('pointerdown', handler);
      window.addEventListener('keydown', handler);
    }
  }

  unlock(): void {
    try {
      this.ctx = this.ctx || new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch { /* no audio */ }
    this.isUnlocked = true;
    // Retry on every call until the track is actually playing (assets may
    // still be loading the first few times).
    this.playMusic();
  }

  /** Set which track the level wants (call from scene create). */
  setMusicKey(key: string): void {
    if (key === this.musicKey) return;
    this.musicKey = key;
    // Switch live if something is already playing
    if (this.music && !this.muted) {
      this.music.stop();
      this.music = undefined;
      this.playMusic();
    }
  }

  /** Looping background music. Starts on first user gesture (autoplay policy). */
  playMusic(key?: string): void {
    const track = key ?? this.musicKey;
    if (key) this.musicKey = key;
    if (!this.isUnlocked || this.muted || this.music) return;
    if (!this.sceneRef.cache.audio.exists(track)) return;
    this.music = this.sceneRef.sound.add(track, { loop: true, volume: 0.35 });
    this.music.play();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    try { localStorage.setItem('gz-muted', this.muted ? '1' : '0'); } catch { /* ignore */ }
    if (this.muted) {
      this.music?.stop();
      this.sceneRef.sound.mute = true;
    } else {
      this.sceneRef.sound.mute = false;
      this.music = undefined; // force restart below
      this.playMusic();
    }
    return this.muted;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  private playTone(
    freqStart: number,
    freqEnd: number,
    duration: number,
    volume: number = 0.2
  ): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain).connect(this.ctx.destination);
    osc.frequency.setValueAtTime(freqStart, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(
      freqEnd,
      this.ctx.currentTime + duration
    );
    gain.gain.setValueAtTime(volume, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  playJump(): void {
    this.playTone(300, 600, 0.15, 0.2);
  }

  playDoubleJump(): void {
    this.playTone(600, 900, 0.12, 0.2);
  }

  playPickup(): void {
    this.playTone(800, 1200, 0.1, 0.3);
  }

  playStoneThrow(): void {
    this.playTone(200, 100, 0.08, 0.1);
  }

  playHit(): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.connect(gain).connect(this.ctx.destination);
    osc.frequency.setValueAtTime(150, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, this.ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.15, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.15);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.15);
  }

  playCheckpoint(): void {
    if (!this.ctx) return;
    // Fanfare: C-E-G
    const notes = [262, 330, 392];
    notes.forEach((freq, i) => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();
      osc.connect(gain).connect(this.ctx!.destination);
      const startTime = this.ctx!.currentTime + i * 0.08;
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.15, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.15);
      osc.start(startTime);
      osc.stop(startTime + 0.15);
    });
  }

  playVictory(): void {
    if (!this.ctx) return;
    // Full fanfare
    const notes = [262, 330, 392, 523, 392, 523, 659, 523];
    notes.forEach((freq, i) => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();
      osc.connect(gain).connect(this.ctx!.destination);
      const startTime = this.ctx!.currentTime + i * 0.12;
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.2, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.2);
      osc.start(startTime);
      osc.stop(startTime + 0.2);
    });
  }

  playDefeat(): void {
    this.playTone(200, 50, 0.5, 0.15);
  }

  stopAll(): void {
    try { (this.sceneRef.sound as any).stopAll(); } catch { /* ignore */ }
  }
}
